import React, { useState, useCallback, useEffect, useRef } from 'react';
import useChatStore from './useChatStore';
import './OuterChatbox.css';
import '../Message/Message.css';
import CloseSharpIcon from '@mui/icons-material/CloseSharp';
import SendSharpIcon from '@mui/icons-material/SendSharp';
import axios from "../../API/axios";
import { markConversationAsRead, parseMessageListResponse } from "../../Services/apiService";
import DoneAllSharpIcon from '@mui/icons-material/DoneAllSharp';
import moment from 'moment';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import DropDownBox from './DropDownBox';
import ThemedEmojiPicker from "../../components/ThemedEmojiPicker";
import AddReactionSharpIcon from "@mui/icons-material/AddReactionSharp";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import ChatForwardModal from "../Message/ChatForwardModal";
import MessageReplyQuote from "../Message/MessageReplyQuote";
import SuccessToast from "../SuccessToast";
import {
  buildReplyApiPayload,
  buildReplyQuoteFromMessage,
  embedReplyInMessage,
  getDisplayMessageText,
  parseEmbeddedReply,
} from "../../utils/messageReplyUtils";


const ChatBox = ({ user }) => {
    const { t } = useTranslation();
    const currentUserProfilPic = localStorage.getItem("profilPic");
    const currentUserUid = localStorage.getItem("userUid");
    const sessionId = localStorage.getItem("sessionId");
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const {
        removeChat,
        toggleChat,
        updateInput,
        storeMessages,
        setMsgIDDirect,
        setChatOnlineStatus,
        messagesList,
    } = useChatStore();
    const [messageId, setMessageId] = useState(null);
    const language = localStorage.getItem("Lang");

    const footerRef = useRef();
    const inputRef = useRef(null);

    const [replyToMessage, setReplyToMessage] = useState(null);
    const [forwardingMessage, setForwardingMessage] = useState(null);
    const [isForwarding, setIsForwarding] = useState(false);
    const [forwardMessageList, setForwardMessageList] = useState([]);
    const [copyToastMessage, setCopyToastMessage] = useState("");
    const [showCopyToast, setShowCopyToast] = useState(false);

    const isOnline =
        user?.toUserIsOnline === true ||
        messagesList?.find((entry) => entry?.chatInfo?.toUserUid === user.userUid)
            ?.chatInfo?.toUserIsOnline === true;

    const clearReplyMessage = useCallback(() => {
        setReplyToMessage(null);
    }, []);

    const handleReplyMessage = useCallback((message) => {
        setReplyToMessage(message);
        setSelectedMessageId(null);
        setShowDropdown(false);
        requestAnimationFrame(() => inputRef.current?.focus());
    }, []);

    const handleForwardMessage = useCallback(async (message) => {
        if (!getDisplayMessageText(message?.msg)?.trim()) return;

        try {
            const response = await axios.post("Message/GetMessageList", {
                deviceType: "Web",
                sessionId,
                langKey: language === "en" ? "" : language,
                pageNumber: 0,
                pageSize: 50,
            });
            setForwardMessageList(parseMessageListResponse(response));
        } catch (error) {
            console.log(error);
            setForwardMessageList([]);
        }

        setForwardingMessage(message);
        setSelectedMessageId(null);
        setShowDropdown(false);
    }, [sessionId, language]);

    const closeForwardModal = useCallback(() => {
        if (isForwarding) return;
        setForwardingMessage(null);
    }, [isForwarding]);

    const handleForwardToContact = useCallback(async (target) => {
        const text = getDisplayMessageText(forwardingMessage?.msg)?.trim();
        const toUserUid = target?.chatInfo?.toUserUid;
        if (!text || !toUserUid) return;

        const threadMsgId = target?.chatInfo?.msgID;
        const payload = {
            deviceType: "Web",
            sessionId,
            type: 1,
            msg: text,
            langKey: language === "en" ? "" : language,
        };

        setIsForwarding(true);
        try {
            if (threadMsgId) {
                await axios.post("Message/ReplyMessage", {
                    ...payload,
                    msgID: threadMsgId,
                });
            } else {
                await axios.post("Message/InitiateMessage", {
                    ...payload,
                    toUserUid,
                });
            }
            setCopyToastMessage(t("Notifications.Message forwarded"));
            setShowCopyToast(true);
            setForwardingMessage(null);
        } catch (error) {
            console.log(error);
        } finally {
            setIsForwarding(false);
        }
    }, [forwardingMessage, sessionId, language, t]);

    const getChatList = useCallback(async () => {
        const data = {
            deviceType: "Web",
            sessionId: sessionId,
            toUserUid: user.userUid,
        };
        axios
            .post(`Message/GetUserChat`, data)
            .then((response) => {
                const res = response.data.responseData.chatList;
                storeMessages(user.userUid, res);
                const messageInfo = response.data.responseData.messageInfo;
                setMessageId(messageInfo?.msgID ?? null);
                if (messageInfo?.msgID) {
                    setMsgIDDirect(user.userUid, messageInfo.msgID);
                }
                if (messageInfo?.toUserIsOnline != null) {
                    setChatOnlineStatus(user.userUid, messageInfo.toUserIsOnline);
                }
                if (user.isOpen) {
                    markConversationAsRead(user.userUid).catch((error) => {
                        console.log("MarkConversationAsRead failed:", error);
                    });
                }
            })
            .catch((error) => {
                console.log(error);
            });
    }, [user.userUid, user.isOpen, sessionId, storeMessages, setMsgIDDirect, setChatOnlineStatus]);

    const sendMessageContent = useCallback(async (rawContent) => {
        const content = rawContent?.trim();
        if (!content) return;

        const activeReply = replyToMessage;
        const quote = activeReply
            ? buildReplyQuoteFromMessage(activeReply, user.fullName, t("Notifications.You"))
            : null;
        const outboundMsg = quote ? embedReplyInMessage(content, quote) : content;

        const messageDetails = {
            deviceType: "Web",
            sessionId: sessionId,
            type: 1,
            msg: outboundMsg,
            langKey: language === "en" ? "" : language,
            ...buildReplyApiPayload(activeReply),
        };

        try {
            if (!user.messages.length) {
                await axios.post("Message/InitiateMessage", {
                    ...messageDetails,
                    toUserUid: user.userUid,
                }).then((response) => {
                    updateInput(user.userUid, "");
                    clearReplyMessage();
                    getChatList();
                    setMsgIDDirect(user.userUid, response.data.responseData.id);
                });
            } else {
                await axios.post("Message/ReplyMessage", {
                    ...messageDetails,
                    msgID: messageId || user.msgID,
                }).then((response) => {
                    if (response.status === 200 && response.data.status === "Success") {
                        updateInput(user.userUid, "");
                        clearReplyMessage();
                        getChatList();
                    }
                });
            }
        } catch (error) {
            console.log(error);
        }
    }, [
        replyToMessage,
        user,
        sessionId,
        language,
        messageId,
        updateInput,
        clearReplyMessage,
        getChatList,
        setMsgIDDirect,
        t,
    ]);

    const sendMessageHandler = useCallback(() => {
        try {
            if (user.inputValue.length) {
                sendMessageContent(user.inputValue);
            }
        } catch (error) {
            console.log(error);
        }
    }, [user.inputValue, sendMessageContent]);

    useEffect(() => {
        getChatList();
    }, [user.userUid]);

    useEffect(() => {
        if (footerRef.current && user.isOpen) {
            footerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }, [user.messages, user.isOpen]);

    const inputChangeHandler = useCallback((userId, value) => {
        try {
            updateInput(userId, value);
        } catch (error) {
            console.log(error);
        }
    }, [updateInput]);

    const handleMoreOptions = (event, record) => {
        try {
            event.stopPropagation();
            setShowDropdown(true);
            setSelectedMessageId((prevId) => (prevId === record.chatID ? null : record.chatID));
        } catch (error) {
            console.log(error);
        }
    };

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const handleEmojiClick = () => {
        setShowEmojiPicker(!showEmojiPicker);
    };
    const handleEmojiClose = () => {
        setShowEmojiPicker(false);
    };

    const getDisplayDate = (date) => {
        const today = moment().startOf("day");
        const msgDate = moment(date).startOf("day");

        if (msgDate.isSame(today, "day")) {
            return "Today";
        } else if (msgDate.isSame(today.clone().subtract(1, "day"), "day")) {
            return "Yesterday";
        } else if (msgDate.isAfter(today.clone().subtract(6, "day"))) {
            return msgDate.format("dddd");
        }
        return msgDate.format("DD-MM-YYYY");
    };

    const renderMessageBody = (record, isOwned) => {
        const { quote } = parseEmbeddedReply(record.msg);
        const displayText = getDisplayMessageText(record.msg);

        return (
            <>
                {quote ? (
                    <MessageReplyQuote
                        quote={{
                            name: quote.name,
                            text: quote.text,
                            replyIsOwned: quote.replyIsOwned,
                        }}
                        isOwnBubble={isOwned}
                    />
                ) : null}
                {displayText}
            </>
        );
    };

    return (
        <div className={`chat-box ${user.isOpen ? 'open' : 'closed'}`}>
            <div className="chat-header" onClick={() => toggleChat(user.userUid)}>
                <div className='outer-chat-box-header-img-fullname'>
                    <a href={`/User/${user.userUid}`} target='_blank' rel="noreferrer">
                        <div className="outer-chat-header-avatar-wrap">
                            <img src={user.profilPic} alt="" width={35} height={35} style={{ borderRadius: "50px" }} />
                            {isOnline ? <span className="outer-chat-online-dot" aria-hidden="true" /> : null}
                        </div>
                    </a>
                    <div className="outer-chat-header-meta">
                        <span className='outer-chat-box-username' title={user.fullName}>
                            {user.fullName}
                        </span>
                        <span className="outer-chat-status f-10 f-400">
                            {isOnline ? t("Notifications.Online") : t("Notifications.Offline")}
                        </span>
                    </div>
                </div>
                <CloseSharpIcon
                    onClick={(e) => {
                        e.stopPropagation();
                        removeChat(user.userUid);
                    }}
                />
            </div>

            {user.isOpen && (
                <div className="chat-messages">
                    {user.messages.map((record, index) => {
                        const previousRecord = user.messages[index - 1];
                        const showDateDivider =
                            index === 0 ||
                            !moment(record.time).isSame(moment(previousRecord?.time), "day");

                        return (
                            <React.Fragment key={record.chatID || index}>
                                {showDateDivider && (
                                    <div className="text-center my-3">
                                        <Tooltip
                                            title={
                                                <p className="text-light">
                                                    {moment.utc(record.time).local().format("DD MMMM YYYY")}
                                                </p>
                                            }
                                            arrow
                                        >
                                            <span className="chat-date-divider px-3 py-1 rounded f-10 f-500">
                                                {getDisplayDate(record.time)}
                                            </span>
                                        </Tooltip>
                                    </div>
                                )}

                                <div
                                    className={`message-container ${record.isOwned ? "owned" : "received"}`}
                                    ref={footerRef}
                                >
                                    {!record.isOwned && (!record.isDeleted || record.isCompleteDeleted === true) && (
                                        <>
                                            <img
                                                src={user.profilPic}
                                                alt="Profile"
                                                className="message-avatar"
                                            />
                                            <div className={`message-bubble f-10 f-400${record.isCompleteDeleted ? " deleted-message" : ""}`}>
                                                {renderMessageBody(record, false)}
                                                <div className="message-time">
                                                    {moment.utc(record.time).local().format("h:mm a")}
                                                </div>
                                            </div>
                                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                                {selectedMessageId === record.chatID && (
                                                    <DropDownBox
                                                        visible={showDropdown}
                                                        setVisibility={setShowDropdown}
                                                        record={record}
                                                        onReplyMessage={handleReplyMessage}
                                                        onForwardMessage={handleForwardMessage}
                                                        onCopySuccess={(message) => {
                                                            setCopyToastMessage(message);
                                                            setShowCopyToast(true);
                                                        }}
                                                    />
                                                )}
                                                <MoreHorizSharpIcon className="material-symbols-sharp" onClick={(e) => handleMoreOptions(e, record)} />
                                            </div>
                                        </>
                                    )}

                                    {record.isOwned && (!record.isDeleted || record.isCompleteDeleted === true) && (
                                        <>
                                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                                {selectedMessageId === record.chatID && (
                                                    <DropDownBox
                                                        visible={showDropdown}
                                                        setVisibility={setShowDropdown}
                                                        record={record}
                                                        onReplyMessage={handleReplyMessage}
                                                        onForwardMessage={handleForwardMessage}
                                                        onCopySuccess={(message) => {
                                                            setCopyToastMessage(message);
                                                            setShowCopyToast(true);
                                                        }}
                                                    />
                                                )}
                                                <MoreHorizSharpIcon className="material-symbols-sharp" onClick={(e) => handleMoreOptions(e, record)} />
                                            </div>
                                            <div className={`message-bubble f-10 f-400${record.isCompleteDeleted ? " deleted-message" : ""}`}>
                                                {renderMessageBody(record, true)}
                                                <div className="message-time">
                                                    {moment.utc(record.time).local().format("h:mm a")}
                                                    {!record.isCompleteDeleted && (
                                                        <span className="message-status">
                                                            {record?.isRead === false ? (
                                                                <DoneAllSharpIcon className="material-symbols-sharp" style={{ width: '15px' }} />
                                                            ) : (
                                                                <DoneAllSharpIcon className="material-symbols-sharp text-read" style={{ width: '15px' }} />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <img
                                                src={currentUserProfilPic}
                                                alt="Profile"
                                                className="message-avatar"
                                            />
                                        </>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {user.isOpen && (
                <div className={`chat-input${replyToMessage ? " has-reply" : ""}`}>
                    {replyToMessage ? (
                        <div className="outer-chat-reply-preview">
                            <div className="outer-chat-reply-preview-content">
                                <div className="f-10 f-600">
                                    {replyToMessage.isOwned ? t("Notifications.You") : user.fullName}
                                </div>
                                <div className="f-10">
                                    {getDisplayMessageText(replyToMessage.msg)}
                                </div>
                            </div>
                            <CloseSharpIcon
                                className="material-symbols-sharp pointer"
                                style={{ fontSize: 16 }}
                                onClick={clearReplyMessage}
                            />
                        </div>
                    ) : null}
                    <div className="outer-chat-input-row">
                        <img
                            src={currentUserProfilPic}
                            alt="user Image"
                            width={30}
                            height={30}
                            style={{ borderRadius: "50px" }}
                        />
                        <textarea
                            ref={inputRef}
                            type="text"
                            rows={1}
                            value={user.inputValue}
                            onChange={(e) => inputChangeHandler(user.userUid, e.target.value)}
                            placeholder={t("Newsfeed.Write messages down here")}
                            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessageHandler())}
                        />
                        <AddReactionSharpIcon
                            className="material-symbols-sharp me-2"
                            onClick={handleEmojiClick}
                            style={{ cursor: "pointer" }}
                        />
                        <SendSharpIcon
                            className='material-symbols-sharp'
                            onClick={() => sendMessageHandler()}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </div>
            )}

            {showEmojiPicker && (
                <div className="messageEmoji">
                    <ThemedEmojiPicker
                        onEmojiClick={(e) => {
                            updateInput(user.userUid, user.inputValue + e.emoji);
                            setShowEmojiPicker(false);
                        }}
                    />
                    <div
                        className="emoji-close"
                        onClick={handleEmojiClose}
                        style={{ cursor: "pointer" }}
                    >
                        <CloseSharpIcon className="material-symbols-sharp" />
                    </div>
                </div>
            )}

            <ChatForwardModal
                show={Boolean(forwardingMessage)}
                onClose={closeForwardModal}
                messageList={forwardMessageList}
                currentUserId={currentUserUid}
                message={forwardingMessage}
                onForward={handleForwardToContact}
                isForwarding={isForwarding}
            />

            <SuccessToast
                show={showCopyToast}
                message={copyToastMessage}
                onClose={() => setShowCopyToast(false)}
            />
        </div>
    );
};

export default ChatBox;
